import type { InitConfiguration } from '@datadog/browser-core';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { isBotUserAgent } from '@/utils/botDetection';

const datadogConfig: InitConfiguration = {
	clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
	env: import.meta.env.MODE.substring(0, import.meta.env.MODE.indexOf('-')),
	service: 'influencer-web',
	sessionSampleRate: 100,
	site: 'datadoghq.com',
	version: APP_VERSION,
};

const ignoredStatusCodes = [0, 404];

const initDatadogRum = (config: InitConfiguration) => {
	datadogRum.init({
		...config,
		allowedTracingUrls: [
			{
				match: import.meta.env.VITE_API_BASE,
				propagatorTypes: ['datadog'],
			},
		],
		applicationId: import.meta.env.VITE_DATADOG_RUM_APP_ID,
		trackLongTasks: true,
		trackResources: true,
		trackUserInteractions: true,
		sessionReplaySampleRate: 100,
		defaultPrivacyLevel: 'mask-user-input',
		beforeSend(event) {
			if (event.type === 'resource' && event.resource.type === 'xhr' && ((event.resource.status_code && ignoredStatusCodes.includes(event.resource.status_code)) || !event.resource.status_code)) {
				return false;
			}
			return true;
		},
	});
	datadogRum.setGlobalContext({ region: import.meta.env.VITE_AWS_REGION });
};

const initDatadogLogs = (config: InitConfiguration) => {
	datadogLogs.init({
		...config,
		forwardErrorsToLogs: true,
		forwardConsoleLogs: ['error'],
		beforeSend: (log) => {
			if (log.http && ignoredStatusCodes.includes(log.http.status_code)) return false;
			return true;
		},
	});

	window.onerror = function (message, source, lineno, colno, error) {
		datadogLogs.logger.error(error?.message || '', {
			error: { stack: error?.stack },
		});
	};
};

const isReportingDisabled = () => import.meta.env.MODE === 'development' || isBotUserAgent();

const setDataDogUser = (userId: string) => {
	if (isReportingDisabled()) return;
	datadogRum.setUser({ id: userId });
};

const clearDataDogUser = () => {
	if (isReportingDisabled()) return;
	datadogRum.clearUser();
};

const initDatadog = () => {
	if (isReportingDisabled()) return;
	initDatadogRum(datadogConfig);
	initDatadogLogs(datadogConfig);
};

export { clearDataDogUser, initDatadog, setDataDogUser };
